import { useEffect, useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Home } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/context/LangContext";
import { orderedMenu } from "@/lib/menu";
import { card } from "@/components/admin/SettingsPanel";

const Row = ({ item, label, i }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const hidden = !item.visible;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`menu-item-${item.key}`}
      className={`flex items-center gap-3 rounded-xl border bg-[#131C2E] px-3 py-3 ${isDragging ? "z-10 border-[#D4AF37] shadow-lg" : "border-[#F4EEE2]/10"}`}
    >
      <button type="button" data-testid={`menu-drag-${i}`} {...attributes} {...listeners} className="cursor-grab touch-none rounded-lg p-1.5 text-[#9AA4B4] hover:text-[#D4AF37] active:cursor-grabbing" aria-label={`Drag ${label}`}>
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="font-mono text-xs text-[#9AA4B4]">{i + 2}</span>
      <span className="flex-1 truncate font-ui text-sm font-semibold text-[#F4EEE2]">{label}</span>
      <span className="font-mono text-[10px] uppercase tracking-widest text-[#9AA4B4]">{item.builtIn ? "Built-in" : "Your page"}</span>
      {hidden && <span className="rounded-full bg-[#2a3449] px-2 py-0.5 font-mono text-[10px] uppercase text-[#4A5568]">Not in menu</span>}
    </li>
  );
};

export const MenuOrder = () => {
  const { settings, setSettings, pages } = useSite();
  const { t } = useLang();
  const [items, setItems] = useState(() => orderedMenu(settings, pages));
  useEffect(() => {
    setItems(orderedMenu(settings, pages));
  }, [settings, pages]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const labelOf = (m) => (m.builtIn ? t(m.label) : m.page.title_en);

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const prev = items;
    const next = arrayMove(items, items.findIndex((m) => m.key === active.id), items.findIndex((m) => m.key === over.id));
    setItems(next);
    try {
      const { data } = await api.put("/settings", { menu_order: next.map((m) => m.key) });
      setSettings((p) => ({ ...p, ...data }));
      toast.success("Menu order saved — live on the site");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="menu-order-panel" className={card}>
      <h3 className="font-display text-2xl text-[#F4EEE2]">Menu order</h3>
      <p className="mt-1 text-sm text-[#B7BFCC]">Drag the handle to move a page up or down. The new order saves and goes live automatically.</p>
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-dashed border-[#F4EEE2]/15 px-3 py-3 text-sm text-[#4A5568]">
        <Home className="ml-1.5 h-4 w-4" /> <span className="font-mono text-xs">1</span> {t("nav.home")} <span className="ml-auto font-mono text-[10px] uppercase tracking-widest">Always first</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((m) => m.key)} strategy={verticalListSortingStrategy}>
          <ul className="mt-2 grid gap-2">
            {items.map((m, i) => <Row key={m.key} item={m} label={labelOf(m)} i={i} />)}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};
